/**
 * The document pages: `/privacy` (PRV-001), `/terms` and `/refunds` (PRV-004). Pages
 * rather than popups: a policy needs an address it can be linked at. Each HTML entry
 * names its copy with `data-document`; the copy lives in config/site.ts and is rendered
 * with textContent only, so no copy can ever become markup.
 *
 * It wears the player's chosen theme, read through the same validated settings store
 * the game uses; anything odd falls back to the default theme.
 */
import './styles/base.css';
import './styles/document.css';
import { DOCUMENT_LINKS, DOCUMENTS, SITE, type DocumentCopy } from './config/site.ts';
import { DEFAULT_SETTINGS, createSettingsStore, type KeyValueStore } from './settings/settings.ts';

function deviceStorage(): KeyValueStore | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

document.documentElement.dataset['theme'] = createSettingsStore(
  deviceStorage(),
  DEFAULT_SETTINGS,
).load().theme;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function link(label: string, href: string, className: string): HTMLAnchorElement {
  const a = el('a', className, label);
  a.href = href;
  // An outside site: no referrer, and no handle back to this page.
  if (!href.startsWith('/')) a.rel = 'noopener noreferrer';
  return a;
}

const page = document.querySelector<HTMLElement>('main[data-document]');
if (page === null) throw new Error('document page: <main data-document> is missing');
const id = DOCUMENT_LINKS.find((d) => d.id === page.dataset['document'])?.id;
if (id === undefined) throw new Error(`document page: unknown "${page.dataset['document']}"`);
const copy: DocumentCopy = DOCUMENTS[id];

const header = el('header', 'document__header');
header.append(
  link(copy.back, '/', 'document__back'),
  el('p', 'document__kicker', SITE.name),
  el('h1', 'document__title', copy.title),
  el('p', 'document__updated', copy.updated),
  el('p', 'document__lede', copy.lede),
);
page.append(header);

for (const section of copy.sections) {
  const block = el('section', 'document__section');
  block.append(el('h2', 'document__heading', section.heading));
  for (const paragraph of section.body) block.append(el('p', 'document__text', paragraph));
  if (section.link !== undefined) {
    const line = el('p', 'document__text');
    line.append(link(section.link.label, section.link.href, 'document__link'));
    block.append(line);
  }
  page.append(block);
}

// The other documents, so each is one tap from the others.
const nav = el('nav', 'document__nav');
nav.setAttribute('aria-label', 'Site documents');
for (const d of DOCUMENT_LINKS) {
  if (d.id === id) continue;
  nav.append(link(d.label, d.href, 'document__nav-link'));
}
page.append(nav);
